import { track } from '@/utils/analytics'
import { SessionKeys, sessionService } from '@/utils/localStorageService'

/**
 * `onboarding_started`, fired at the moment onboarding is ENTERED rather than at the moment a landing
 * button is clicked — and fired exactly once per tab.
 *
 * WHY IT MOVED. The event used to live in the three landing CTAs and nowhere else, so a campaign
 * pointed straight at /onboard produced none of them: the funnel's own first step was missing for the
 * traffic it was built to measure, and a working campaign read as a dead one. Measured on the FB run
 * that goes past the landing.
 *
 * WHY ARRIVAL AND NOT THE CLICK. A click fires before the route changes, so the old shape could count
 * more starts than `page_viewed` counted arrivals — a funnel whose first step exceeds its own second
 * is unreadable. Arrival is also the thing the word means.
 *
 * SO THE DOOR IS REMEMBERED, NOT REPORTED. The landing writes which button was pressed and returns;
 * the /onboard screen reports the entry and reads that door back as `source`. A person who never came
 * through a landing button reports `direct`, which is the value the campaign traffic produces and the
 * one the old shape could not express at all.
 *
 * sessionStorage, not localStorage: a second visit tomorrow is a second start, and a reload of
 * /onboard is not.
 */

/** The landing buttons, by name. A new door means a new value here and nowhere else. */
export type OnboardingDoor = 'hero' | 'nav' | 'closing'

/** Called by a landing CTA instead of tracking. Records the door and nothing else. */
export const rememberOnboardingDoor = (door: OnboardingDoor): void => {
  sessionService.setItem(SessionKeys.onboardDoor, door)
}

/**
 * Called once by the /onboard screen on mount. Reports the entry with the door that led to it, or
 * `direct` when there was none. Repeated calls in the same tab do nothing, so a reload, a re-render
 * or a second mount cannot inflate the count.
 */
export const trackOnboardingStarted = (): void => {
  if (sessionService.getItem(SessionKeys.onboardStarted)) return
  sessionService.setItem(SessionKeys.onboardStarted, '1')
  track('onboarding_started', {
    source: sessionService.getItem(SessionKeys.onboardDoor) ?? 'direct',
  })
}
